import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

// Entry point for all OAuth redirects and email confirmation links.
// We own the org-membership check here instead of delegating to AuthContext,
// because the Supabase client exchanges the OAuth code and fires SIGNED_IN
// before any onAuthStateChange subscriber is registered (PKCE timing).
// By also calling getSession() immediately, we catch the session regardless
// of whether SIGNED_IN or INITIAL_SESSION arrives first.

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState('');
  const handled = useRef(false); // prevent double-processing from getSession + onAuthStateChange

  useEffect(() => {
    const handleSession = async (session: Session) => {
      if (handled.current) return;
      handled.current = true;

      // If the user arrived via Google OAuth from an invite page, a pending
      // invite token was stored in localStorage before the redirect.
      const pendingToken = localStorage.getItem('pendingInviteToken');
      if (pendingToken) {
        localStorage.removeItem('pendingInviteToken');
        try {
          const { data: inv } = await supabase
            .from('organization_invitations')
            .select('org_id, email, role, role_id, status, expires_at')
            .eq('token', pendingToken)
            .single();

          if (inv && inv.status === 'pending' && new Date(inv.expires_at) > new Date()) {
            if (inv.email.toLowerCase() !== session.user.email?.toLowerCase()) {
              await supabase.auth.signOut();
              setErrorMsg('This invitation was sent to a different email address. Please sign in with the invited email.');
              return;
            }
            // Accept the invitation — creates the org membership
            await supabase.from('organization_members').insert({
              org_id:    inv.org_id,
              user_id:   session.user.id,
              role:      inv.role,
              role_id:   inv.role_id,
              is_active: true,
            });
            await supabase
              .from('organization_invitations')
              .update({ status: 'accepted' })
              .eq('token', pendingToken);
          }
        } catch {
          // Non-fatal: invite may have been already accepted; fall through to membership check
        }
      }

      // Check org membership. Select is_active too, but fall back gracefully
      // if the column doesn't exist yet (migration not yet run).
      const { data: memberData, error: memberError } = await supabase
        .from('organization_members')
        .select('org_id, is_active')
        .eq('user_id', session.user.id)
        .single();

      // PGRST116 = no rows found; anything else may be a schema issue (column missing, etc.)
      const noMembership = !memberData && (!memberError || memberError.code === 'PGRST116');
      if (noMembership) {
        await supabase.auth.signOut();
        const isGoogle = session.user.app_metadata?.provider === 'google';
        setErrorMsg(
          isGoogle
            ? 'Your Google account is not linked to any organization. Please ask your admin to invite you.'
            : 'No organization found for this account. Please contact your admin.'
        );
        return;
      }

      // If is_active column exists and user is disabled
      if (memberData && memberData.is_active === false) {
        await supabase.auth.signOut();
        setErrorMsg('Your account has been disabled. Please contact your organization admin.');
        return;
      }

      // memberData found (or schema error that we can't interpret as "no membership") → allow in
      navigate('/dashboard', { replace: true });
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        session?.user &&
        (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'USER_UPDATED')
      ) {
        handleSession(session);
      }
    });

    // Catches the session when the PKCE code was exchanged before our subscription
    // was registered (common: client processes the URL code on module init).
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) { setErrorMsg(error.message); return; }
      if (session?.user) handleSession(session);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  if (errorMsg) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>✗</div>
          <h2 style={{ color: '#c33', margin: '0 0 8px' }}>Access Denied</h2>
          <p style={styles.sub}>{errorMsg}</p>
          <button style={styles.btn} onClick={() => navigate('/login')}>Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.spinner} />
        <h2 style={{ margin: '16px 0 8px' }}>Signing you in…</h2>
        <p style={styles.sub}>Verifying your access.</p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#FFF4E6',
  },
  card: {
    background: 'white',
    borderRadius: 16,
    padding: '48px 40px',
    textAlign: 'center',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    maxWidth: 380,
    width: '100%',
  },
  sub: { color: '#888', fontSize: 14, margin: '0 0 24px' },
  btn: {
    padding: '10px 24px',
    background: 'var(--accent-color)',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid #f0f0f0',
    borderTop: '3px solid var(--accent-color)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto',
  },
};

const styleEl = document.createElement('style');
styleEl.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
document.head.appendChild(styleEl);

export default AuthCallback;
